import { useState, useContext } from 'react';
import { FaUser, FaPhone, FaEnvelope, FaMapMarkerAlt, FaTshirt, FaFileAlt, FaPaperPlane, FaWhatsapp, FaInstagram, FaFacebook } from 'react-icons/fa';
import { MdEmail, MdLocationOn } from 'react-icons/md';
import Emailjs from '../services/Emailjs';
import { DContext } from '../store/DataContext';

function Contact() {
    const { showAlert } = useContext(DContext);
    const [formData, setFormData] = useState({
        name: '',
        contact: '',
        email: '',
        city: '',
        product: '',
        subject: '',
        description: ''
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const productOptions = [
        { value: '', label: 'Select a Product Category' },
        { value: 'mens-tshirts', label: "Men's T-Shirts" },
        { value: 'mens-trackpants', label: "Men's Track Pants" },
        { value: 'mens-tracksuits', label: "Men's Tracksuits" },
        { value: 'mens-polos', label: "Men's Polos" },
        { value: 'womens-kurtis', label: "Women's Feeding Kurtis & Sets" },
        { value: 'womens-tops-sets', label: "Women's Tops & Bottom Sets" },
        { value: 'womens-innerwear', label: "Women's Panties & Briefs" },
        { value: 'womens-maternity', label: "Women's Maternity Topwear" },
        { value: 'kids-tshirts-boys', label: "Kids T-Shirts (Boys)" },
        { value: 'kids-tshirts-girls', label: "Kids T-Shirts (Girls)" },
        { value: 'kids-polos', label: "Kids Polos" },
        { value: 'bulk-order', label: 'Bulk/Custom Order' },
        { value: 'other', label: 'Other' }
    ];

    const validateForm = () => {
        const newErrors = {};

        // Name validation (required, min 2 chars, only letters and spaces)
        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        } else if (formData.name.trim().length < 2) {
            newErrors.name = 'Name must be at least 2 characters';
        } else if (!/^[a-zA-Z\s]+$/.test(formData.name)) {
            newErrors.name = 'Name can only contain letters and spaces';
        }

        // Contact validation (required, 10 digits)
        if (!formData.contact.trim()) {
            newErrors.contact = 'Contact number is required';
        } else if (!/^[6-9]\d{9}$/.test(formData.contact.replace(/\s/g, ''))) {
            newErrors.contact = 'Enter a valid 10-digit mobile number';
        }

        // Email validation (optional, but must be valid if provided)
        if (formData.email.trim()) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData.email)) {
                newErrors.email = 'Enter a valid email address';
            }
        }

        // City validation (required, min 2 chars)
        if (!formData.city.trim()) {
            newErrors.city = 'City is required';
        } else if (formData.city.trim().length < 2) {
            newErrors.city = 'City must be at least 2 characters';
        } else if (!/^[a-zA-Z\s]+$/.test(formData.city)) {
            newErrors.city = 'City can only contain letters and spaces';
        }

        // Product validation (required)
        if (!formData.product) {
            newErrors.product = 'Please select a product category';
        }

        // Subject validation (required, min 3 chars)
        if (!formData.subject.trim()) {
            newErrors.subject = 'Subject is required';
        } else if (formData.subject.trim().length < 3) {
            newErrors.subject = 'Subject must be at least 3 characters';
        }

        // Description is optional, no validation needed

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            showAlert('Please fix the errors in the form');
            return;
        }

        setIsSubmitting(true);

        try {
            await Emailjs({
                from_name: formData.name,
                contact: formData.contact,
                email: formData.email || 'Not provided',
                city: formData.city,
                product: productOptions.find(p => p.value === formData.product)?.label || formData.product,
                subject: formData.subject,
                message: formData.description || 'No additional details provided'
            });

            await showAlert('Thank you! Your inquiry has been submitted successfully. We will contact you soon.');

            // Reset form
            setFormData({
                name: '',
                contact: '',
                email: '',
                city: '',
                product: '',
                subject: '',
                description: ''
            });
        } catch (error) {
            showAlert('Failed to send your inquiry. Please try again or contact us directly.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            {/* Hero Section */}
            <section className="relative bg-primary-600 text-white py-16 px-4 overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl animate-pulse"></div>
                    <div className="absolute bottom-10 right-10 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse delay-700"></div>
                </div>

                <div className="max-w-6xl mx-auto text-center relative z-10">
                    <h1 className="text-4xl md:text-5xl font-bold mb-4">Get In Touch</h1>
                    <p className="text-lg md:text-xl opacity-90">
                        Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
                    </p>
                </div>
            </section>

            <div className="max-w-6xl mx-auto px-4 py-12 md:py-16">
                <div className="grid lg:grid-cols-5 gap-8 lg:gap-12">
                    {/* Contact Information - Left Side */}
                    <div className="lg:col-span-2 space-y-6">
                        <div className="bg-white rounded-2xl shadow-lg p-8 transform hover:scale-105 transition-all duration-300">
                            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                                <MdLocationOn className="text-primary-600 mr-3" />
                                Our Location
                            </h2>

                            <div className="space-y-4">
                                <div className="flex items-start">
                                    <FaMapMarkerAlt className="text-primary-600 mt-1 mr-3 flex-shrink-0" />
                                    <div>
                                        <h3 className="font-semibold text-gray-800">SS Tex Garments</h3>
                                        <p className="text-gray-600">Tiruppur, Tamil Nadu</p>
                                        <p className="text-gray-600">India</p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <MdEmail className="text-primary-600 mt-1 mr-3 flex-shrink-0" />
                                    <div>
                                        <h3 className="font-semibold text-gray-800">Email</h3>
                                        <p className="text-gray-600">sstexonline@gmail.com</p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <FaPhone className="text-primary-600 mt-1 mr-3 flex-shrink-0" />
                                    <div>
                                        <h3 className="font-semibold text-gray-800">Phone</h3>
                                        <p className="text-gray-600">+91 95852 84627</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Social Links */}
                        <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-2xl shadow-lg p-8 text-white">
                            <h3 className="text-xl font-bold mb-4">Connect With Us</h3>
                            <div className="flex space-x-4">
                                <a href="#" className="bg-white/20 hover:bg-white/30 p-3 rounded-full transition-all transform hover:scale-110">
                                    <FaWhatsapp className="text-2xl" />
                                </a>
                                <a href="#" className="bg-white/20 hover:bg-white/30 p-3 rounded-full transition-all transform hover:scale-110">
                                    <FaInstagram className="text-2xl" />
                                </a>
                                <a href="#" className="bg-white/20 hover:bg-white/30 p-3 rounded-full transition-all transform hover:scale-110">
                                    <FaFacebook className="text-2xl" />
                                </a>
                            </div>
                        </div>

                        {/* Quick Info */}
                        <div className="bg-blue-50 rounded-2xl p-6 border border-blue-200">
                            <h3 className="font-bold text-gray-800 mb-3">🚚 All-India Delivery</h3>
                            <p className="text-gray-600 text-sm">
                                We ship to all states across India with fast and reliable logistics.
                            </p>
                        </div>

                        {/* Map / Location */}
                        <div className="bg-white rounded-2xl shadow-lg p-6">
                            <h3 className="font-bold text-gray-800 mb-4">Find Us On Map</h3>
                            <div className="relative w-full h-64 md:h-80 rounded-xl overflow-hidden">
                                <iframe
                                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1438266.1781095786!2d75.01140349374997!3d11.081656500000022!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba9079aceb7595b%3A0x7345754720d65bcc!2sS%20S%20Tex!5e1!3m2!1sen!2sin!4v1764015721287!5m2!1sen!2sin"
                                    className="absolute inset-0 w-full h-full border-0"
                                    allowFullScreen
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                    title="SS Tex Location"
                                ></iframe>
                            </div>
                        </div>
                    </div>

                    {/* Contact Form - Right Side */}
                    <div className="lg:col-span-3">
                        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10">
                            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">Send Us a Message</h2>

                            <form onSubmit={handleSubmit} className="space-y-5">
                                {/* Name Field */}
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-2">
                                        Name <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <FaUser className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            className={`w-full pl-12 pr-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all ${errors.name ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                            placeholder="Enter your full name"
                                        />
                                    </div>
                                    {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                                </div>

                                {/* Contact Field */}
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-2">
                                        Contact Number <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <FaPhone className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="tel"
                                            name="contact"
                                            value={formData.contact}
                                            onChange={handleChange}
                                            className={`w-full pl-12 pr-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all ${errors.contact ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                            placeholder="Enter 10-digit mobile number"
                                            maxLength="10"
                                        />
                                    </div>
                                    {errors.contact && <p className="text-red-500 text-sm mt-1">{errors.contact}</p>}
                                </div>

                                {/* Email Field (Optional) */}
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-2">
                                        Email <span className="text-gray-400 text-sm">(Optional)</span>
                                    </label>
                                    <div className="relative">
                                        <FaEnvelope className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            className={`w-full pl-12 pr-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all ${errors.email ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                            placeholder="your.email@example.com"
                                        />
                                    </div>
                                    {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                                </div>

                                {/* City Field */}
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-2">
                                        City <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <FaMapMarkerAlt className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="text"
                                            name="city"
                                            value={formData.city}
                                            onChange={handleChange}
                                            className={`w-full pl-12 pr-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all ${errors.city ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                            placeholder="Enter your city"
                                        />
                                    </div>
                                    {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
                                </div>

                                {/* Product Dropdown */}
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-2">
                                        Product Category <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <FaTshirt className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        <select
                                            name="product"
                                            value={formData.product}
                                            onChange={handleChange}
                                            className={`w-full pl-12 pr-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all appearance-none bg-white ${errors.product ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                        >
                                            {productOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    {errors.product && <p className="text-red-500 text-sm mt-1">{errors.product}</p>}
                                </div>

                                {/* Subject Field */}
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-2">
                                        Subject <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <FaFileAlt className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="text"
                                            name="subject"
                                            value={formData.subject}
                                            onChange={handleChange}
                                            className={`w-full pl-12 pr-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all ${errors.subject ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                            placeholder="What is your inquiry about?"
                                        />
                                    </div>
                                    {errors.subject && <p className="text-red-500 text-sm mt-1">{errors.subject}</p>}
                                </div>

                                {/* Description Field (Optional) */}
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-2">
                                        Description <span className="text-gray-400 text-sm">(Optional)</span>
                                    </label>
                                    <textarea
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        rows="4"
                                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all resize-none"
                                        placeholder="Additional details about your inquiry..."
                                    />
                                </div>

                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className={`w-full bg-primary-600 hover:bg-primary-700 text-white font-bold py-4 rounded-lg transition-all transform hover:scale-105 flex items-center justify-center space-x-2 shadow-lg ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                                        }`}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                            <span>Sending...</span>
                                        </>
                                    ) : (
                                        <>
                                            <FaPaperPlane />
                                            <span>Send Message</span>
                                        </>
                                    )}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Contact;